import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Rocket, Users, GraduationCap } from "lucide-react";

const highlights = [
  {
    icon: <Rocket size={22} />,
    title: "Real Product Development",
    description:
      "Build scalable products used by businesses across multiple industries using modern technologies and best engineering practices.",
  },
  {
    icon: <Users size={22} />,
    title: "Collaborative Environment",
    description:
      "Work closely with experienced developers, designers, QA engineers, and project managers in an agile environment.",
  },
  {
    icon: <GraduationCap size={22} />,
    title: "Continuous Learning",
    description:
      "Gain exposure to new technologies, participate in knowledge-sharing sessions, and continuously improve your technical skills.",
  },
  {
    icon: <Sparkles size={22} />,
    title: "Innovation Driven",
    description:
      "Contribute your ideas, solve challenging business problems, and build digital experiences that create measurable impact.",
  },
];

const JobOverview = ({ job }) => {
  return (
    <section className="relative overflow-hidden font-saira">
      {/* Main Container */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          initial={{
            opacity: 0,
            y: 35,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.6,
          }}
          className="mx-auto max-w-5xl text-center"
        >
          <p
            className="text-xs font-bold uppercase tracking-[0.3em] sm:text-sm"
            style={{ color: "var(--brand-primary)" }}
          >
            [ ABOUT THIS ROLE ]
          </p>

          <h2 className="mt-4 text-3xl font-bold leading-tight theme-text-primary sm:text-4xl lg:text-5xl">
            Shape The Future With{" "}
            <span style={{ color: "var(--brand-primary)" }}>WebBocket</span>
          </h2>

          <p className="mx-auto mt-6 max-w-7xl text-base leading-8 theme-text-muted sm:text-lg sm:leading-9">
            As a{" "}
            <span className="font-semibold theme-text-primary">
              {job.title}
            </span>
            , you'll work on innovative software solutions that help businesses
            transform digitally. You'll collaborate with talented professionals,
            solve real-world challenges, and contribute to products that deliver
            meaningful impact while continuously growing your technical and
            professional expertise.
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="mt-10 grid gap-4 sm:gap-5 lg:gap-6 md:grid-cols-2">
          {highlights.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{
                opacity: 0,
                y: 30,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.5,
                delay: index * 0.12,
              }}
              whileHover={{
                y: -6,
              }}
              className="
                group
                theme-card
                rounded-2xl
                border
                theme-border
                p-5
                sm:p-6
                transition-all
                duration-300
              "
              style={{
                boxShadow: "0 10px 30px var(--shadow-color)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor =
                  "color-mix(in srgb, var(--brand-primary) 40%, transparent)";
                e.currentTarget.style.backgroundColor = "var(--bg-tertiary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border-primary)";
                e.currentTarget.style.backgroundColor = "var(--card-bg)";
              }}
            >
              {/* Card Header */}
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className="
                    flex
                    h-11
                    w-11
                    sm:h-12
                    sm:w-12
                    shrink-0
                    items-center
                    justify-center
                    rounded-xl
                    transition-all
                    duration-300
                  "
                  style={{
                    backgroundColor:
                      "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                    color: "var(--brand-primary)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--brand-primary)";
                    e.currentTarget.style.color = "#ffffff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "color-mix(in srgb, var(--brand-primary) 10%, transparent)";
                    e.currentTarget.style.color = "var(--brand-primary)";
                  }}
                >
                  {item.icon}
                </div>

                <h3 className="text-lg font-semibold theme-text-primary sm:text-xl">
                  {item.title}
                </h3>
              </div>

              {/* Description */}
              <p className="mt-4 pl-14 text-sm leading-7 theme-text-muted sm:pl-16 sm:text-[15px] sm:leading-8">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default JobOverview;